import { createSelector } from '@reduxjs/toolkit';
import { selectPersonalClockedInWorkRecords } from 'store/records/selectors/records.selectors';
import { getUserProjectPreferenceKey } from '../utils/userProjectPreferences';
import {
  selectLatestUserActiveProjects,
  selectProjectIdsLatestClockedInTo,
  selectProjectsArrayWithJoinedData,
  selectUserProjectPreferences,
} from './projectsSelectors';

export const selectPersonalClockedInRecordsProjectIds = createSelector(
  [selectPersonalClockedInWorkRecords],
  (records) => records.map((record) => record.project.id)
);

export const selectLatestProjectsClockedInTo = createSelector(
  [
    selectProjectsArrayWithJoinedData,
    selectProjectIdsLatestClockedInTo,
    selectPersonalClockedInRecordsProjectIds,
  ],
  (projects, latestProjectIds, personalClockedInProjectIds) =>
    projects.filter(
      (project) =>
        latestProjectIds.includes(project.id) &&
        project.businessTypeEnum !== 'EMPLOYER' &&
        project.status !== 'ARCHIVED' &&
        !personalClockedInProjectIds.includes(project.id)
    )
);

// Drives the navbar Timer drawer's "Latest projects" list, split into two
// groups: { clockedIn, rest }. Clocked-in projects (rendered as ProjectListItems
// with the edit-record button) go to the TOP, separated from the rest by a
// divider — mirroring how the source list divides the employer project from the
// rest. Any currently clocked-in project missing from the activity list (e.g.
// older than 30 days, or an employer project) is force-included so active work
// is always visible. Within each group, favorited projects are hoisted first
// (preserving the existing order otherwise).
export const selectLatestActiveProjectsForTimerDrawer = createSelector(
  [
    selectLatestUserActiveProjects,
    selectProjectsArrayWithJoinedData,
    selectPersonalClockedInRecordsProjectIds,
    selectUserProjectPreferences,
  ],
  (activeProjects, allProjects, personalClockedInProjectIds, preferences) => {
    const clockedInIdSet = new Set(personalClockedInProjectIds);

    const activeFiltered = activeProjects.filter(
      (project) =>
        project.businessTypeEnum !== 'EMPLOYER' &&
        project.status !== 'ARCHIVED'
    );
    const activeIdSet = new Set(activeFiltered.map((project) => project.id));

    const clockedInMissing = allProjects.filter(
      (project) =>
        clockedInIdSet.has(project.id) && !activeIdSet.has(project.id)
    );

    // Stable hoist of favorited projects to the front of a group.
    const favoritesFirst = (projects) => {
      const favorites = [];
      const others = [];
      projects.forEach((project) => {
        const isFavorite = Boolean(
          preferences[getUserProjectPreferenceKey(project)]?.favorite
        );
        (isFavorite ? favorites : others).push(project);
      });
      return [...favorites, ...others];
    };

    return {
      clockedIn: favoritesFirst([
        ...clockedInMissing,
        ...activeFiltered.filter((project) => clockedInIdSet.has(project.id)),
      ]),
      rest: favoritesFirst(
        activeFiltered.filter((project) => !clockedInIdSet.has(project.id))
      ),
    };
  }
);
